/*
  TCG dialog safety net, v2 (2026-09-11).

  v1 (2026-09-07) fixed the close button that never fired dialog.close() on
  iOS and resized the drawer with 100dvh. Customers still reported the cart
  drawer opening with its title row and X above the visible screen, so the
  drawer looked like a page with no header and no way out.

  v2 stops relying on CSS units for the visible area and pins every open
  drawer to window.visualViewport instead: top = the viewport's offset,
  height = its visible height. That is re-applied whenever the visual
  viewport changes (Safari toolbar collapsing or expanding, keyboard,
  rotation, pinch zoom) and after every cart re-render. On top of that:

    - any programmatic scroll of the dialog box or its inner column
      (both overflow:hidden, so a customer can never drag them back) is
      undone on the spot via their scroll events;
    - the header row is measured against the visual viewport after every
      change, and if it is still not on screen a floating X is shown,
      positioned in the viewport, that closes the drawer without waiting
      for any transition;
    - closing strips ?cart=true from the address bar so a refresh or a
      back-navigation does not reopen a drawer the customer just closed
      (templates/cart.liquid redirects /cart to /?cart=true).

  Falcon's own DialogComponent is untouched.
*/
(function () {
  var GRACE_MS = 350;
  var vv = window.visualViewport || null;
  var pinScheduled = false;

  function openDrawers() {
    return Array.prototype.slice.call(document.querySelectorAll("dialog[open].drawer"));
  }

  function stripCartParam() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has("cart")) return;
      url.searchParams.delete("cart");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    } catch (e) { /* ignore */ }
  }

  function forceClose(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove("closing");
    try { dialog.close(); } catch (e) { /* already closed */ }
    if (!document.querySelector("dialog[open]")) document.body.style.overflow = "";
  }

  function armFallback(dialog) {
    if (!dialog) return;
    setTimeout(function () {
      if (dialog.open && dialog.classList.contains("closing")) forceClose(dialog);
    }, GRACE_MS);
  }

  // Undo any scroll of the two overflow:hidden boxes. A focus() or a
  // scrollIntoView() from any script can still scroll them, and nothing the
  // customer does can scroll them back.
  function settle(dialog) {
    if (!dialog || !dialog.open) return;
    var inner = dialog.querySelector(".dialog-inner");
    if (dialog.scrollTop) dialog.scrollTop = 0;
    if (inner && inner.scrollTop) inner.scrollTop = 0;
  }

  function guard(dialog) {
    if (dialog.__tcgGuarded) return;
    dialog.__tcgGuarded = true;
    var reset = function () { settle(dialog); };
    dialog.addEventListener("scroll", reset, { passive: true });
    dialog.addEventListener("transitionend", function () { pin(dialog); });
    var inner = dialog.querySelector(".dialog-inner");
    if (inner) inner.addEventListener("scroll", reset, { passive: true });
  }

  // Is the title row (with the X) actually inside the visible viewport?
  function checkHeader(dialog) {
    var header = dialog.querySelector(".dialog-header");
    if (!header) return;
    var rect = header.getBoundingClientRect();
    var top = vv ? vv.offsetTop : 0;
    var bottom = top + (vv ? vv.height : window.innerHeight);
    var offscreen = rect.height === 0 || rect.bottom < top + 8 || rect.top > bottom - 8;
    dialog.classList.toggle("tcg-header-offscreen", offscreen);

    var float = dialog.querySelector(".tcg-drawer-close-float");
    if (float && vv) {
      // Position the rescue X inside the visual viewport, not the layout one.
      float.style.top = Math.round(vv.offsetTop + 10) + "px";
      float.style.left = Math.round(vv.offsetLeft + vv.width - 54) + "px";
      float.style.right = "auto";
    }
  }

  // Size and place the drawer to exactly the visible part of the screen.
  function pin(dialog) {
    if (!dialog || !dialog.open || !dialog.classList.contains("drawer")) return;
    var top = 0;
    var height = window.innerHeight;
    var scaled = false;
    if (vv) {
      top = Math.max(0, Math.round(vv.offsetTop));
      height = Math.round(vv.height);
      scaled = vv.scale && Math.abs(vv.scale - 1) > 0.01;
    }
    if (!height) return;

    var inner = dialog.querySelector(".dialog-inner");
    dialog.style.top = top + "px";
    dialog.style.bottom = "auto";
    dialog.style.height = height + "px";
    dialog.style.maxHeight = height + "px";
    if (inner) {
      inner.style.height = height + "px";
      inner.style.maxHeight = height + "px";
    }
    if (scaled) {
      // Pinch-zoomed: follow the zoomed window horizontally too.
      dialog.style.left = Math.round(vv.offsetLeft) + "px";
      dialog.style.right = "auto";
      dialog.style.width = Math.round(vv.width) + "px";
      dialog.style.maxWidth = "none";
    } else {
      dialog.style.left = "";
      dialog.style.right = "";
      dialog.style.width = "";
      dialog.style.maxWidth = "";
    }
    settle(dialog);
    checkHeader(dialog);
  }

  function unpin(dialog) {
    ["top", "bottom", "height", "maxHeight", "left", "right", "width", "maxWidth"].forEach(function (p) {
      dialog.style[p] = "";
    });
    var inner = dialog.querySelector(".dialog-inner");
    if (inner) { inner.style.height = ""; inner.style.maxHeight = ""; }
    dialog.classList.remove("tcg-header-offscreen");
  }

  function pinAll() {
    if (pinScheduled) return;
    pinScheduled = true;
    requestAnimationFrame(function () {
      pinScheduled = false;
      openDrawers().forEach(pin);
    });
  }

  function onOpen(dialog) {
    guard(dialog);
    pin(dialog);
    // Layout can still move in the first frames (fonts, images, the
    // slide-in transition, Safari's toolbar reacting to the modal).
    requestAnimationFrame(function () { pin(dialog); });
    setTimeout(function () { pin(dialog); }, 120);
    setTimeout(function () { pin(dialog); }, 400);
    setTimeout(function () { pin(dialog); }, 900);
    // Slow heartbeat while open: catches a shift that arrives without any
    // event we listen for. One cheap measurement a second.
    if (dialog.__tcgTick) clearInterval(dialog.__tcgTick);
    dialog.__tcgTick = setInterval(function () {
      if (!dialog.open) { clearInterval(dialog.__tcgTick); dialog.__tcgTick = null; return; }
      pin(dialog);
    }, 1000);
  }

  // 1. Close button, backdrop tap, or the floating rescue X.
  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var rescue = target.closest(".tcg-drawer-close-float");
    if (rescue) {
      event.preventDefault();
      event.stopPropagation();
      forceClose(rescue.closest("dialog"));
      return;
    }

    var closeBtn = target.closest("[data-dialog-close]");
    if (closeBtn) { armFallback(closeBtn.closest("dialog")); return; }

    var dialog = target.closest("dialog");
    if (!dialog || !dialog.open) return;
    var inner = dialog.querySelector(".dialog-inner");
    if (inner && !inner.contains(target)) armFallback(dialog);
  }, true);

  // 2. React to every dialog opening or closing.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      var d = m.target;
      if (d.tagName !== "DIALOG") return;
      if (d.open) {
        if (d.classList.contains("closing")) d.classList.remove("closing");
        if (d.classList.contains("drawer")) onOpen(d);
        else settle(d);
      } else {
        if (d.__tcgTick) { clearInterval(d.__tcgTick); d.__tcgTick = null; }
        if (d.classList.contains("drawer")) unpin(d);
        if (d.id === "cart-drawer") stripCartParam();
        if (!document.querySelector("dialog[open]")) document.body.style.overflow = "";
      }
    });
  }).observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ["open"] });

  // Drawers that were already open before this script ran (e.g. ?cart=true
  // handled by a script that executed earlier).
  openDrawers().forEach(onOpen);

  // 3. Visible-area changes: Safari toolbar, keyboard, rotation, zoom.
  if (vv) {
    vv.addEventListener("resize", pinAll);
    vv.addEventListener("scroll", pinAll);
  }
  window.addEventListener("resize", pinAll);
  window.addEventListener("orientationchange", function () {
    pinAll();
    setTimeout(pinAll, 300);
  });

  // 4. After the cart re-renders, and whenever focus moves inside a drawer.
  window.addEventListener("cart:rendered", function () {
    pinAll();
    setTimeout(pinAll, 300);
  });
  document.addEventListener("focusin", function (event) {
    var d = event.target && event.target.closest && event.target.closest("dialog[open].drawer");
    if (d) { settle(d); checkHeader(d); }
  }, true);

  // 5. Escape key: make sure the native cancel actually closes it.
  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    openDrawers().forEach(armFallback);
  });

  // 6. Coming back to the page (bfcache) with a dead half-closed dialog.
  window.addEventListener("pageshow", function () {
    document.querySelectorAll("dialog[open].closing").forEach(forceClose);
    pinAll();
  });
})();
